import { bindable } from "aurelia-templating";
import { transient, autoinject, computedFrom } from "aurelia-framework";
import { LookupValue, LookupValueResponse, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { LookupControl } from "../../../components/lookups/lookup-control/lookup-control";
import { Reflection } from "../../../../utilities/reflection";

export type MultipleLookupValues = { [value: number]: number | true };
export type MultipleLookupExclusiveAnswers = { [id: number]: true };

@autoinject
@transient()
export class MultipleLookupsControl {

    private _count: number = 0;
    private _internalValuesChanged = false;

    @bindable
    public parameters: string = "";

    @bindable
    public readonly: boolean;

    @bindable
    public disabled: boolean;

    @bindable
    public query: string;

    @bindable
    public values: MultipleLookupValues;

    @bindable
    public internalValues: number[];

    @bindable
    public isCheckboxStyle: boolean = true;

    @bindable
    public exclusiveAnswers: string | number | number[];

    public itemSource: LookupValue[] = null;
    public exclusiveAnswersSet: MultipleLookupExclusiveAnswers;

    @computedFrom(Reflection.getPropertyPath<LookupControl>(x => x.itemSource),
        Reflection.getPropertyPath<LookupControl>(x => x.selectedValue)
    )
    public get itemSourceFiltered() {
        let filtered = this.itemSource;

        if (filtered) {
            filtered = filtered.filter(x => x.isActive || this.values[x.id] == true);
        }

        return filtered;
    }

    private get _internalValues(): number[] {
        const internalValues = Object.keys(this.values).filter(k => this.values[k] != undefined)
            .map(k => Number(k))
            .filter(id => !isNaN(id));

        return internalValues;
    }

    constructor(private element: Element, protected lookupsClientService: LookupClientService) {
    }

    public queryChanged(newValue: string, oldValue: string): void {
        this.lookupsClientService.unsubscribeForLookupsRefresh(oldValue, this.parameters, this.loadData);
        this.lookupsClientService.subscribeForLookupsRefresh(newValue, this.parameters, this.loadData);
        this.loadData();
    }

    public parametersChanged(newValue: string, oldValue: string): void {
        this.lookupsClientService.unsubscribeForLookupsRefresh(this.query, oldValue, this.loadData);
        this.lookupsClientService.subscribeForLookupsRefresh(this.query, newValue, this.loadData);
        this.loadData();
    }

    public readonlyChanged(): void {
        this.loadData();
    }

    public disabledChanged(): void {
        this.loadData();
    }

    public internalValuesChanged() {
        this._internalValuesChanged = !this._internalValuesChanged;

        if (this._internalValuesChanged) {
            this.internalValues = this._internalValues;
        }
    }

    public exclusiveAnswersChanged() {
        if (!this.exclusiveAnswers) {
            this.exclusiveAnswersSet = [];
        }

        this.exclusiveAnswersSet = {};

        if (Array.isArray(this.exclusiveAnswers)) {
            this.exclusiveAnswers.forEach(ea => {
                this.exclusiveAnswersSet[ea] = true;
            })
            return;
        }

        switch (typeof (this.exclusiveAnswers)) {
            case 'number':
                this.exclusiveAnswersSet[this.exclusiveAnswers] = true;
                break;
            case 'string':
                {
                    const exclusiveAnswers = this.exclusiveAnswers.split(',')
                    .map(a => Number(a))
                    .filter(n => !isNaN(n));

                    exclusiveAnswers.forEach(ea => {
                        this.exclusiveAnswersSet[ea] = true;
                    });
                    break;
                }
        }
    }

    isAttached: boolean;

    attached(): void {
        this.isAttached = true;
        this.loadData();
    }

    protected loadData = (): void => {
        if (!this.isAttached) return;
        this._count++;
        const localCount = this._count;
        if (this.query) {
            const lookupValueRequest = this.createLookupValueRequest();
            this.lookupsClientService.getLookup(lookupValueRequest, "default")
                .then((response: LookupValueResponse) => {
                    if (localCount !== this._count) {
                        return;
                    }
                    if (response) {
                        const cloneResponse = response.allValuesIncludingInactive.slice(0);
                        this.itemSource = cloneResponse;
                    } else {
                        this.itemSource = [];
                    }
                    setTimeout(() => {
                        const valuesLoadedEvent = new CustomEvent("values-loaded", { detail: { values: this.itemSource.slice(0) }, bubbles: true });
                        this.element.dispatchEvent(valuesLoadedEvent);
                    });
                }).catch((e) => {
                    if (localCount !== this._count) {
                        return;
                    }
                    console.error(e);
                    this.itemSource = [];
                });
        }
    }

    protected createLookupValueRequest(): LookupValueRequest {
        const request = new LookupValueRequest();
        request.lookupValueQuery = this.query;
        request.parameters = this.parameters;
        return request;
    }

    public onValuesChanged() {
        this._internalValuesChanged = true;
        this.internalValues = this._internalValues;
    }

    detached() {
        this.lookupsClientService.unsubscribeForLookupsRefresh(this.query, this.parameters, this.loadData);
    }
}
